// Angular Core Modules
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';

// Third-party Modules
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  StoreRouterConnectingModule,
  RouterStateSerializer,
  FullRouterStateSerializer
} from '@ngrx/router-store';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { Angulartics2Module } from 'angulartics2';
import { DragulaModule } from 'ng2-dragula';
import { ToastrModule } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// Application Modules
import { SharedModule } from './shared-module/shared.module';
import { CoreModule } from './core-module/core.module';
import { BreakpointModule } from './core-module/modules/breakpoint.module';
import { AppRoutingModule } from './app-routing.module';
import { GraphQLModule } from './graphql.module';
import { ChatViaModule } from './features/chat-via/chat-via.module';

// Application Components
import { AppComponent } from './app.component';

// State Management
import { reducers, metaReducers, CustomSerializer } from './state';
import { AuthEffects } from './state/auth/auth.effects';
import { ReturnLinkEffects } from './state/auth/return-link.effects';
import { CognitoAuthEffects } from './state/cognito-auth/cognito-auth.effects';
import { CertificationEffects } from './state/certification/certification.effects';
import { DeviceEffects } from './state/device/device.effects';
import { ErrorEffects } from './state/error/error.effects';
import { FavoriteOccupationsEffects } from './state/favorite-occupations/favorite-occupations.effects';
import { GoalEffects } from './state/goal/goal.effects';
import { HotSpotsEffects } from './state/hot-spots/hot-spots.effects';
import { InstitutionEffects } from './state/institution/institution.effects';
import { InstitutionProgramEffects } from './state/institution-program/institution-program.effects';
import { LicenseEffects } from './state/license/license.effects';
import { LoaderEffects } from './state/loader/loader.effects';
import { LocationEffects } from './state/location/location.effects';
import { MaintanceEffects } from './state/maintance/maintance.effects';
import { MajorEffects } from './state/major/major.effects';
import { NewsEffects } from './state/news/news.effects';
import { SystemStatusEffects } from './state/system-status/system-status.effects';
import { OccupationCardsEffects } from './state/occupation-cards/occupation-cards.effects';
import { OccupationDetailEffects } from './state/occupation-detail/occupation-detail.effects';
import { OccupationEffects } from './state/occupation/occupation.effects';
import { PathwayCardsEffects } from './state/pathway-cards/pathway-cards.effects';
import { PathwayEffects } from './state/pathway/pathway.effects';
import { PowerBiEffects } from './state/powerbi/powerbi.effects';
import { TrackerEffects } from './state/tracker/tracker.effects';
import { UserProfileEffects } from './state/user-profile/user-profile.effects';
import { UserEffects } from './state/user/user.effects';
import { FavoriteMilitaryOccupationsEffects } from './state/favorite-military-occupations/favorite-occupations.effects';
import { AiChatEffects } from './state/ai-chat/ai-chat.effects';

// Services, Interceptors, and Utilities
import {
  AuthInterceptor,
  SessionInterceptor,
  SpinnerInterceptor,
} from './core-module/interceptors';
import {
  CustomMatIconRegistryService,
  FontAwesomeIconRegistryService
} from './core-module/services/icon-registry';

// Environment Configurations
import { environment } from '../environments/environment';

export function initializeIconRegistries(
  customMatIconRegistryService: CustomMatIconRegistryService,
  fontAwesomeIconRegistryService: FontAwesomeIconRegistryService
) {
  return () => {
    customMatIconRegistryService.init();
    fontAwesomeIconRegistryService.init();
  };
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    CoreModule,
    BreakpointModule.forRoot(),
    AppRoutingModule,
    ChatViaModule,

    Angulartics2Module.forRoot({
      pageTracking: {
        clearQueryParams: true
      }
    }),
    DragulaModule.forRoot(),
    NgCircleProgressModule.forRoot({}),
    ToastrModule.forRoot({
      closeButton: true,
      maxOpened: 1,
      timeOut: 5000,
      positionClass: 'toast-center-center',
      progressBar: true,
      progressAnimation: 'decreasing',
      preventDuplicates: true
    }),

    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule.forRoot({
      serializer: FullRouterStateSerializer,
      stateKey: 'router'
    }),
    EffectsModule.forRoot([
      MaintanceEffects, // MaintanceEffects MUST GO FIRST SO THAT CHECKING SITE IS MAINTAINING OR NOT.
      AuthEffects, // AuthEffects MUST GO FIRST SO THAT LOADING SESSION AND ESTABLISHING USER PROFILE HAPPENS AS SOON AS POSSIBLE
      ReturnLinkEffects, // AUTH.9. Before CognitoAuthEffects, whose init dispatches processReturnSession
      CognitoAuthEffects, // CognitoAuthEffects MUST GO FIRST SO THAT CHECKING AUTHENTICATION STATUS HAPPENS AS SOON AS POSSIBLE
      UserProfileEffects,
      CertificationEffects,
      GoalEffects,
      FavoriteOccupationsEffects,
      FavoriteMilitaryOccupationsEffects,
      DeviceEffects,
      ErrorEffects,
      HotSpotsEffects,
      InstitutionEffects,
      InstitutionProgramEffects,
      LicenseEffects,
      LoaderEffects,
      LocationEffects,
      MajorEffects,
      NewsEffects,
      SystemStatusEffects,
      OccupationCardsEffects,
      OccupationDetailEffects,
      OccupationEffects,
      PathwayCardsEffects,
      PathwayEffects,
      PowerBiEffects,
      TrackerEffects,
      UserEffects,
      AiChatEffects
    ]),
    HammerModule,
    FontAwesomeModule,
    GraphQLModule,
    HttpClientModule
  ],
  declarations: [AppComponent],
  providers: [
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { timezone: '+0000' }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIconRegistries,
      deps: [CustomMatIconRegistryService, FontAwesomeIconRegistryService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
